import React, { useCallback, useEffect, useState } from "react";
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import type { ApiJob, ApiMonteCarloJob } from "@tradingdroid/frontends-types";

import { JobStatusBadge } from "@src/components/JobStatusBadge";
import { tdApi } from "@src/services/api/tdApi";
import { getApiErrorMessage } from "@src/utils/apiError";
import { RobustnessAggregates } from "./RobustnessAggregates";
import { RobustnessLaunchForm } from "./RobustnessLaunchForm";
import {
  TERMINAL_JOB_STATUSES,
  formatPValue,
  hasViewableResult,
  isPartialRun,
} from "./robustnessFormat";

interface Props {
  baselineBacktestId: number;
  strategyId: number;
}

const isMonteCarloJob = (job: ApiJob): job is ApiMonteCarloJob =>
  job.job_type_name === "monte_carlo";

const modeLabel = (job: ApiMonteCarloJob): string =>
  job.config?.mode === "permutation" ? "Permutation test" : "Execution stress";

// One-line headline for a viewable run: the ranking p-value (permutation) or the scenario count.
const runHeadline = (job: ApiMonteCarloJob): string => {
  const mc = job.result?.data?.monte_carlo;
  if (!mc) return "—";
  if (mc.mode === "permutation") return `p = ${formatPValue(mc.permutation?.p_value)}`;
  const n = mc.execution_stress?.scenario_table?.length ?? 0;
  return `${n} scenario${n === 1 ? "" : "s"}`;
};

/** The Backtest Robustness tab: launch a monte_carlo run against this backtest and view past runs. */
export const RobustnessTab: React.FC<Props> = ({ baselineBacktestId, strategyId }) => {
  const [runs, setRuns] = useState<ApiMonteCarloJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // No server-side filter on a nested config field (see issue #393), so fetch recent monte_carlo
      // jobs and keep the ones whose baseline is this backtest.
      const response = await tdApi.getJobs({
        limit: 100,
        sort_by: "id:DESC",
        q: "job_type_name.EQ:'monte_carlo'",
      });
      const mine = (response?.items ?? [])
        .filter(isMonteCarloJob)
        .filter((job) => job.config?.baseline_backtest_id === baselineBacktestId);
      setRuns(mine);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [baselineBacktestId]);

  useEffect(() => {
    load();
  }, [load]);

  // Light auto-refresh while any listed run is still working, so a launched run flips to completed
  // without a manual reload. Stops once everything is terminal.
  useEffect(() => {
    const anyActive = runs.some((job) => !TERMINAL_JOB_STATUSES.has(job.status_name));
    if (!anyActive) return;
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [runs, load]);

  const onLaunched = useCallback(
    (job: ApiJob) => {
      if (isMonteCarloJob(job)) setRuns((current) => [job, ...current]);
      load();
    },
    [load],
  );

  const selected = runs.find((job) => job.id === selectedId) ?? null;
  const selectedResult =
    selected && hasViewableResult(selected) ? selected.result?.data?.monte_carlo : undefined;

  return (
    <div className="RobustnessTab">
      <CCard className="mb-3">
        <CCardHeader>Run robustness analysis</CCardHeader>
        <CCardBody>
          <RobustnessLaunchForm
            baselineBacktestId={baselineBacktestId}
            strategyId={strategyId}
            onLaunched={onLaunched}
          />
        </CCardBody>
      </CCard>

      <CCard className="mb-3">
        <CCardHeader className="d-flex align-items-center justify-content-between">
          <span>Past runs</span>
          <CButton size="sm" color="secondary" variant="outline" onClick={load} disabled={loading}>
            {loading ? <CSpinner size="sm" /> : "Refresh"}
          </CButton>
        </CCardHeader>
        <CCardBody>
          {error && <CAlert color="warning">Could not load runs: {error}</CAlert>}
          {!error && runs.length === 0 && (
            <div className="text-body-secondary">
              {loading ? "Loading…" : "No robustness runs yet for this backtest."}
            </div>
          )}
          {runs.length > 0 && (
            <CTable small hover responsive className="RobustnessRunsTable align-middle mb-0">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>Run</CTableHeaderCell>
                  <CTableHeaderCell>Analysis</CTableHeaderCell>
                  <CTableHeaderCell>Status</CTableHeaderCell>
                  <CTableHeaderCell>Result</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {runs.map((job) => (
                  <CTableRow key={job.id} active={job.id === selectedId}>
                    <CTableDataCell>
                      {/* A viewable run's id opens its aggregates below; clicking another swaps it. */}
                      {hasViewableResult(job) ? (
                        <CButton
                          color="link"
                          className="RobustnessRunLink p-0"
                          onClick={() => setSelectedId(job.id)}
                        >
                          #{job.id}
                        </CButton>
                      ) : (
                        <span className="text-body-secondary">#{job.id}</span>
                      )}
                    </CTableDataCell>
                    <CTableDataCell>{modeLabel(job)}</CTableDataCell>
                    <CTableDataCell>
                      <JobStatusBadge status={job.status_name} />
                      {isPartialRun(job) && (
                        <CBadge
                          className="RobustnessPartialBadge ms-2"
                          color="warning"
                          // Both, never one or the other: the reason is a raw token —
                          // `children_lost:5`, or `process_checkpoint` on the reap path — so on its
                          // own it explains nothing, and the sentence on its own drops the only
                          // detail the payload gave.
                          title={
                            "This run covers less than the planned experiment." +
                            (job.result?.data?.monte_carlo?.stop_reason
                              ? ` (${job.result.data.monte_carlo.stop_reason})`
                              : "")
                          }
                        >
                          Partial
                        </CBadge>
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {/* A partial run's headline is deliberately NOT its p-value. That number is
                          computed over the children that finished, so printing it here would be the
                          same string a completed run prints, with only a badge to tell them apart. */}
                      {isPartialRun(job)
                        ? "partial — open to view"
                        : hasViewableResult(job)
                          ? runHeadline(job)
                          : "—"}
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          )}
        </CCardBody>
      </CCard>

      {selected && selectedResult && (
        <CCard>
          <CCardHeader>Run #{selected.id} aggregates</CCardHeader>
          <CCardBody>
            <RobustnessAggregates
              result={selectedResult}
              maxDrawdownPct={selected.config?.max_acceptable_drawdown_pct}
            />
          </CCardBody>
        </CCard>
      )}
      {selected && !selectedResult && (
        <CAlert color="info">Run #{selected.id} has no aggregate result to show.</CAlert>
      )}
    </div>
  );
};
